import { Fragment } from 'react'
import { Transition } from '@headlessui/react'
import { CheckCircle, AlertTriangle, Bell, Settings, X, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Toast Component - Card-style redesign
 *
 * Types:
 * - success: Green check circle
 * - error: Red alert triangle
 * - warning: Amber alert triangle
 * - info/alert: Blue bell
 * - system: Gray settings
 *
 * Features:
 * - Card-style with icon circle and progress bar
 * - Auto-dismiss with animated progress indicator
 * - Optional action link
 * - Manual dismiss button
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'alert' | 'system'

export interface ToastData {
  id: string
  type: ToastType
  title: string
  description?: string
  duration?: number
  actionText?: string
  actionHref?: string
  actionOnClick?: () => void
}

export interface ToastProps extends ToastData {
  isVisible: boolean
  onDismiss: (id: string) => void
}

const toastConfig: Record<
  ToastType,
  {
    icon: typeof CheckCircle
    circleBg: string
    iconColor: string
    progressColor: string
  }
> = {
  success: {
    icon: CheckCircle,
    circleBg: 'bg-green-50',
    iconColor: 'text-green-500',
    progressColor: 'bg-green-500',
  },
  error: {
    icon: AlertTriangle,
    circleBg: 'bg-red-50',
    iconColor: 'text-red-500',
    progressColor: 'bg-red-500',
  },
  warning: {
    icon: AlertTriangle,
    circleBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    progressColor: 'bg-amber-500',
  },
  info: {
    icon: Bell,
    circleBg: 'bg-blue-50',
    iconColor: 'text-accent',
    progressColor: 'bg-accent',
  },
  alert: {
    icon: Bell,
    circleBg: 'bg-blue-50',
    iconColor: 'text-accent',
    progressColor: 'bg-accent',
  },
  system: {
    icon: Settings,
    circleBg: 'bg-gray-100',
    iconColor: 'text-gray-400',
    progressColor: 'bg-gray-400',
  },
}

export function Toast({
  id,
  type,
  title,
  description,
  duration = 5000,
  actionText,
  actionHref,
  actionOnClick,
  isVisible,
  onDismiss,
}: ToastProps) {
  const config = toastConfig[type]
  const Icon = config.icon

  return (
    <Transition
      as={Fragment}
      show={isVisible}
      enter="transform ease-out duration-medium transition"
      enterFrom="translate-x-full opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transform ease-in duration-small transition"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-full opacity-0"
    >
      <div
        className="w-full sm:max-w-[380px] md:min-w-[360px] pointer-events-auto rounded-xl shadow-lg bg-white border border-gray-200 overflow-hidden"
        role="alert"
        aria-live="polite"
      >
        <div className="p-3.5 px-4">
          <div className="flex items-start gap-3">
            {/* Icon circle */}
            <div
              className={cn(
                'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                config.circleBg
              )}
            >
              <Icon className={cn('w-5 h-5', config.iconColor)} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{title}</p>
              {description && (
                <p className="mt-0.5 text-sm text-gray-500">{description}</p>
              )}
              {actionText && (actionHref || actionOnClick) && (
                <button
                  type="button"
                  onClick={() => {
                    if (actionOnClick) {
                      actionOnClick()
                    } else if (actionHref) {
                      window.location.href = actionHref
                    }
                    onDismiss(id)
                  }}
                  className="inline-flex items-center gap-1 mt-1.5 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                >
                  {actionText}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Dismiss button */}
            <button
              type="button"
              onClick={() => onDismiss(id)}
              className={cn(
                'flex-shrink-0 inline-flex rounded-md p-1',
                'text-gray-400 hover:text-gray-500',
                'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
                'transition-colors duration-small'
              )}
            >
              <span className="sr-only">Затвори</span>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {duration > 0 && (
          <div className="h-[3px] bg-gray-100">
            <div
              className={cn('h-full', config.progressColor)}
              style={{
                animation: `toast-progress ${duration}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>
    </Transition>
  )
}

/**
 * ToastContainer - Renders toasts in a fixed position
 */
export interface ToastContainerProps {
  toasts: (ToastData & { isVisible: boolean })[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <>
      {/* CSS animation for progress bar */}
      <style>{`
        @keyframes toast-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <div
        aria-live="assertive"
        className="fixed right-0 left-0 bottom-[4.25rem] md:bottom-0 md:left-auto flex flex-col items-end px-3 pb-3 sm:px-4 sm:py-6 md:p-6 pointer-events-none z-toast"
      >
        <div className="flex flex-col-reverse items-end space-y-4 space-y-reverse w-full">
          {toasts.map((toast) => (
            <Toast key={toast.id} {...toast} onDismiss={onDismiss} />
          ))}
        </div>
      </div>
    </>
  )
}
